import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getFormSchema } from 'forms';
import {
  submitAwardApplication,
  updateAwardApplication,
} from 'services/submissionService';
import { trackEvent } from 'services/firebase';
import { useRoleInfo } from 'hooks/useRoleInfo';
import {
  ORGANIZATION_CATEGORIES,
  INDIVIDUAL_CATEGORIES,
} from 'constants/evaluation';
import type { Submission } from 'types/evaluation';

interface FormField {
  name: string;
  type: string;
  label: string;
  required: boolean;
  defaultValue?: any;
  displayValue?: string;
  readonly?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
}

interface FormSchema {
  categories: string[];
  fieldMappings: Record<string, Record<string, string>>;
  fieldsByCategory: Record<string, FormField[]>;
}

// Build a reverse mapping: Strapi field name → form field name
const buildReverseFieldMapping = (
  fieldMapping: Record<string, string>
): Record<string, string> => {
  const reverse: Record<string, string> = {};
  Object.entries(fieldMapping).forEach(([formField, strapiField]) => {
    reverse[strapiField] = formField;
  });
  return reverse;
};

// Map a Submission object back to form field names using the reverse mapping
const submissionToFormValues = (
  submission: Partial<Submission>,
  category: string,
  originalSchema: FormSchema
): Record<string, any> => {
  const fieldMapping = originalSchema.fieldMappings[category];
  if (!fieldMapping) return {};
  const reverse = buildReverseFieldMapping(fieldMapping);
  const values: Record<string, any> = {};
  Object.entries(submission).forEach(([strapiField, value]) => {
    const formField = reverse[strapiField] ?? strapiField;
    if (value !== null && value !== undefined) {
      values[formField] = value;
    }
  });
  return values;
};

// Function to modify schema for qualification form
const createQualificationSchema = (
  originalSchema: FormSchema,
  userRoleType?: string,
  userPillar?: string
): FormSchema => {
  const modifiedSchema: FormSchema = JSON.parse(JSON.stringify(originalSchema)); // Deep clone

  // Modify fieldsByCategory for each category
  Object.keys(modifiedSchema.fieldsByCategory).forEach(category => {
    const fields = modifiedSchema.fieldsByCategory[category];

    if (INDIVIDUAL_CATEGORIES.includes(category)) {
      // For individual categories: remove ALL textarea fields
      modifiedSchema.fieldsByCategory[category] = fields.filter(
        field => field.type !== 'textarea'
      );
    } else if (ORGANIZATION_CATEGORIES.includes(category)) {
      // For organization categories: remove all textarea fields EXCEPT project name fields
      modifiedSchema.fieldsByCategory[category] = fields.filter(field => {
        if (field.type !== 'textarea') return true;
        // Keep project name related fields
        return (
          field.name === 'project_name' || field.name === 'ai_project_name'
        );
      });
    }

    // For PIC_PILLAR users, pre-select their pillar and make it readonly
    if (userRoleType === 'PIC_PILLAR' && userPillar) {
      modifiedSchema.fieldsByCategory[category] =
        modifiedSchema.fieldsByCategory[category].map(field => {
          if (field.name === 'pillar' && field.options) {
            return {
              ...field,
              defaultValue: userPillar,
              readonly: true,
              options: field.options.filter(
                option => option.value === userPillar
              ),
            };
          }
          return field;
        });
    }
  });

  return modifiedSchema;
};

// Transform form data to Strapi format for qualification (with file uploads)
const transformQualificationDataForStrapi = (
  formData: Record<string, any>,
  category: string,
  originalSchema: FormSchema
) => {
  const fieldMapping = originalSchema.fieldMappings[category];

  if (!fieldMapping) {
    throw new Error(`No field mapping found for category: ${category}`);
  }

  const transformedData: Record<string, any> = {};

  // Add the category to the transformed data
  transformedData.category = category;

  // Transform each field according to the mapping
  Object.entries(formData).forEach(([formFieldName, value]) => {
    const strapiFieldName = fieldMapping[formFieldName];
    console.log(
      `🔄 Field mapping: ${formFieldName} -> ${strapiFieldName || 'NO MAPPING'}`
    );

    if (strapiFieldName) {
      transformedData[strapiFieldName] = value;
      console.log(
        `✅ Mapped: ${formFieldName} (${value}) -> ${strapiFieldName}`
      );
    } else {
      // Handle special fields that aren't in the mapping (like award_material, video_url, competition_stage)
      if (
        ['award_material', 'video_url', 'competition_stage'].includes(
          formFieldName
        )
      ) {
        transformedData[formFieldName] = value;
        console.log(
          `✅ Special field: ${formFieldName} -> ${formFieldName} (${value})`
        );
      } else {
        console.log(`❌ Unmapped field: ${formFieldName} (${value})`);
      }
    }
  });

  return transformedData;
};

// Function to add default values for removed fields during submission
const addDefaultValuesForRemovedFields = (
  formData: Record<string, any>,
  category: string,
  originalSchema: FormSchema
) => {
  const originalFields = originalSchema.fieldsByCategory[category] || [];
  const modifiedFields =
    createQualificationSchema(originalSchema).fieldsByCategory[category] || [];

  // Get field names that were in original but not in modified (i.e., removed)
  const originalFieldNames = new Set(originalFields.map(f => f.name));
  const modifiedFieldNames = new Set(modifiedFields.map(f => f.name));
  const removedFieldNames = [...originalFieldNames].filter(
    name => !modifiedFieldNames.has(name)
  );

  // Add default values for removed fields (but preserve existing values)
  const enhancedFormData = { ...formData };
  removedFieldNames.forEach(fieldName => {
    // Only add "default" if the field doesn't already have a value
    if (!enhancedFormData[fieldName] || enhancedFormData[fieldName] === '') {
      enhancedFormData[fieldName] = 'default';
    }
  });

  return enhancedFormData;
};

export const useQualificationForm = (
  editSubmission?: (Partial<Submission> & { documentId: string }) | null
) => {
  const { userRoleType, userPillar } = useRoleInfo();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isEditMode = !!editSubmission;
  const [selectedCategory, setSelectedCategory] = useState<string>(
    editSubmission?.category ?? ''
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [processingStage, setProcessingStage] = useState<string>('');

  const originalSchema = getFormSchema('smdd') as FormSchema;
  const qualificationSchema = createQualificationSchema(
    originalSchema,
    userRoleType,
    userPillar
  );
  const categories = qualificationSchema.categories;

  const initialValues =
    isEditMode && editSubmission.category
      ? submissionToFormValues(
          editSubmission,
          editSubmission.category,
          originalSchema
        )
      : null;

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setSubmitted(false);
    setError(null);
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    setIsSubmitting(true);
    setError(null);
    setProcessingStage('Validating submission data...');

    try {
      // Add default values for removed fields to maintain backend compatibility
      const enhancedFormData = addDefaultValuesForRemovedFields(
        formData,
        selectedCategory,
        originalSchema
      );

      console.log(
        '📋 Enhanced form data before transformation:',
        enhancedFormData
      );

      setProcessingStage('Processing files and extracting information...');

      const transformedData = transformQualificationDataForStrapi(
        enhancedFormData,
        selectedCategory,
        originalSchema
      );

      console.log('🔄 Transformed data after mapping:', transformedData);

      const cleanedData = { ...transformedData };

      if (
        cleanedData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedData.email)
      ) {
        throw new Error(`Invalid email format: ${cleanedData.email}`);
      }

      const requiredFields = [
        'submission_type',
        'pillar',
        'org_unit',
        'full_name',
        'title',
        'email',
        'category',
      ];
      const missingFields = requiredFields.filter(
        field => !cleanedData[field] || cleanedData[field].trim() === ''
      );
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const textFields = [
        'description',
        'technology_used',
        'innovation_description',
        'impact_and_results',
      ];
      textFields.forEach(field => {
        if (cleanedData[field] && cleanedData[field].length > 5000) {
          cleanedData[field] = cleanedData[field].substring(0, 5000) + '...';
        }
      });

      Object.keys(cleanedData).forEach(key => {
        if (typeof cleanedData[key] === 'string') {
          cleanedData[key] = cleanedData[key].trim();
        }
      });

      let recaptchaToken: string;
      if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
        recaptchaToken = 'dev-bypass';
      } else {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA not ready');
        }
        recaptchaToken = await executeRecaptcha(
          isEditMode ? 'update_qualification' : 'submit_qualification'
        );
      }
      cleanedData.recaptchaToken = recaptchaToken;

      setProcessingStage('Submitting to awards system...');
      if (isEditMode && editSubmission?.documentId) {
        await updateAwardApplication(editSubmission.documentId, cleanedData);
      } else {
        const result = await submitAwardApplication(cleanedData);
      }

      setProcessingStage('Finalizing submission...');
      trackEvent('form_submission', {
        form_name: isEditMode
          ? 'SMDD Qualification Update'
          : 'SMDD Qualification Submission',
        category: selectedCategory,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setIsSubmitting(false);
      setProcessingStage('');
    }
  };

  const selectedFields = selectedCategory
    ? qualificationSchema.fieldsByCategory[selectedCategory]
    : null;
  const dynamicFormSchema = selectedFields ? { fields: selectedFields } : null;

  const getProgressStep = () => {
    const step = !selectedCategory ? 1 : selectedCategory && !submitted ? 2 : 3;
    return step;
  };

  return {
    selectedCategory,
    submitted,
    error,
    isSubmitting,
    processingStage,
    categories,
    handleCategoryChange,
    handleFormSubmit,
    dynamicFormSchema,
    progressStep: getProgressStep(),
    isEditMode,
    initialValues,
  };
};
